import { createClientSharedComposable, getStore } from '@global-store/store'
import type { CustomerStore } from '@global-store/stores/customer/types'
import { useLocalStorage } from '@vueuse/core'
import { sprykerApiClient } from 'internal/api-client'
import { defineStore } from 'pinia'

import type { AddressInterface, CustomerInterface } from '@fond-of/spryker-api'

export const useCustomerStore = createClientSharedComposable(() =>
  defineStore('legacy-customer', {
    state: (): CustomerStore => ({
      customer: null,
      accountExistsStore: useLocalStorage('accountExists', false),
      token: null,
    }),

    getters: {
      isLoading: (state) => state.customer === null,
      isAuthenticated: (state) => !!state.customer,

      displayName: (state) =>
        state.customer?.firstName || state.customer?.email?.split('@')?.[0],

      /**
       * @deprecated removed without replacement
       */
      accountExists: (state) => !!state.customer || state.accountExistsStore,
      /**
       * @deprecated removed without replacement
       */
      defaultBillingAddress: (state) => {
        return state.customer?.addresses?.find(
          // @ts-ignore -> not used in Shopify
          ({ isDefaultBilling }) => isDefaultBilling
        ) as undefined | AddressInterface
      },

      /**
       * @deprecated removed without replacement
       */
      defaultShippingAddress: (state) => {
        return state.customer?.addresses?.find(
          // @ts-ignore -> not used in Shopify
          ({ isDefaultShipping }) => isDefaultShipping
        ) as undefined | AddressInterface
      },
    },

    actions: {
      login(token?: string | null) {
        this.token = token
      },

      logout() {
        this.customer = undefined
        this.token = undefined
      },

      setCustomer(customer?: CustomerInterface) {
        this.customer = customer
      },

      waitForCustomer() {
        return new Promise((resolve) =>
          this.$subscribe(() => !this.isLoading && resolve(this), {
            immediate: true,
          })
        )
      },

      setAccountExists(accountExists: boolean) {
        this.accountExistsStore = accountExists
      },

      async updateAddress(address: AddressInterface) {
        if (!this.customer?.id || !address.id) {
          throw "Address can't be updated because customer or address ID is not set"
        }

        const updatedAddress = await sprykerApiClient.updateCustomerAddress(
          this.customer.id,
          address.id,
          address
        )

        const addressIndex =
          this.customer.addresses?.findIndex((address) => {
            address.id === updatedAddress.id
          }) ?? -1

        if (addressIndex !== -1 && this.customer.addresses) {
          this.customer.addresses[addressIndex] = updatedAddress
        }

        return updatedAddress
      },
    },

    hydrate: (storeState) => {
      // @ts-expect-error: https://github.com/microsoft/TypeScript/issues/43826
      storeState.accountExists = useLocalStorage('accountExists', false)
    },
  })(getStore())
)
